if (!customElements.get('selling-points-carousel')) {
  customElements.define('selling-points-carousel', class SellingPointsCarousel extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.timer = null;
      this.intersectionObserver = null;
      this.isInViewport = false;
      this.isPointerInside = false;
      this.isFocusInside = false;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;
      this.isTouchTracking = false;
      this.isAnimating = false;
      this.pendingDirection = 0;
      this.positionIndex = 0;
      this.handleResize = this.handleResize.bind(this);
      this.handlePrevClick = this.handlePrevClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchMove = this.handleTouchMove.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      this.handleTouchCancel = this.handleTouchCancel.bind(this);
      this.handleIntersection = this.handleIntersection.bind(this);
      this.pauseAutoplay = this.pauseAutoplay.bind(this);
      this.resumeAutoplay = this.resumeAutoplay.bind(this);
      this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
    }

    connectedCallback() {
      this.isAnimating = false;
      this.pendingDirection = 0;
      this.viewport = this.querySelector('[data-carousel-viewport]');
      this.track = this.querySelector('[data-carousel-track]');
      if (this.track) {
        this.track.querySelectorAll('[data-carousel-clone]').forEach((clone) => clone.remove());
      }
      this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
      this.prevButton = this.querySelector('[data-slider-previous]');
      this.nextButton = this.querySelector('[data-slider-next]');
      this.progressBar = this.querySelector('[data-slider-progress]');
      this.navigation = this.querySelector('.slider-navigation-wrap');
      this.loop = !this.navigation || this.navigation.dataset.sliderLoop !== 'false';
      this.autoplayEnabled = this.dataset.autoplay === 'true' && !(window.Shopify && window.Shopify.designMode);
      this.interval = Number(this.dataset.interval) || 5000;
      this.setupLoopTrack();

      if (this.track) {
        this.track.addEventListener('transitionend', this.handleTransitionEnd);
      }

      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.handlePrevClick);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.handleNextClick);
      }

      if (this.viewport) {
        this.viewport.addEventListener('touchstart', this.handleTouchStart, { passive: true });
        this.viewport.addEventListener('touchmove', this.handleTouchMove, { passive: true });
        this.viewport.addEventListener('touchend', this.handleTouchEnd);
        this.viewport.addEventListener('touchcancel', this.handleTouchCancel);
      }

      this.addEventListener('mouseenter', this.pauseAutoplay);
      this.addEventListener('mouseleave', this.resumeAutoplay);
      this.addEventListener('focusin', this.pauseAutoplay);
      this.addEventListener('focusout', this.resumeAutoplay);
      window.addEventListener('resize', this.handleResize);

      this.setActive(0, { instant: true, force: true });
      this.observeVisibility();
    }

    disconnectedCallback() {
      this.stopAutoplay();

      if (this.track) {
        this.track.removeEventListener('transitionend', this.handleTransitionEnd);
      }

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }

      if (this.prevButton) {
        this.prevButton.removeEventListener('click', this.handlePrevClick);
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.handleNextClick);
      }

      if (this.viewport) {
        this.viewport.removeEventListener('touchstart', this.handleTouchStart);
        this.viewport.removeEventListener('touchmove', this.handleTouchMove);
        this.viewport.removeEventListener('touchend', this.handleTouchEnd);
        this.viewport.removeEventListener('touchcancel', this.handleTouchCancel);
      }

      this.removeEventListener('mouseenter', this.pauseAutoplay);
      this.removeEventListener('mouseleave', this.resumeAutoplay);
      this.removeEventListener('focusin', this.pauseAutoplay);
      this.removeEventListener('focusout', this.resumeAutoplay);
      window.removeEventListener('resize', this.handleResize);
    }

    handleResize() {
      this.isAnimating = false;
      this.pendingDirection = 0;
      this.positionIndex = this.loop && this.slides.length > 1 ? this.slides.length + this.currentIndex : this.currentIndex;
      this.updateTrackPosition(true);
      this.updateProgress();
    }

    handleNextClick() {
      if (!this.slides.length) {
        return;
      }

      this.goBy(1);
      this.restartAutoplay();
    }

    handlePrevClick() {
      if (!this.slides.length) {
        return;
      }

      this.goBy(-1);
      this.restartAutoplay();
    }

    handleTouchStart(event) {
      if (this.slides.length < 2 || event.touches.length !== 1) {
        return;
      }

      const touch = event.touches[0];
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;
      this.isTouchTracking = true;
      this.stopAutoplay();
    }

    handleTouchMove(event) {
      if (!this.isTouchTracking || event.touches.length !== 1) {
        return;
      }

      const touch = event.touches[0];
      this.touchDeltaX = touch.clientX - this.touchStartX;
      this.touchDeltaY = touch.clientY - this.touchStartY;
    }

    handleTouchEnd() {
      if (!this.isTouchTracking) {
        return;
      }

      const absDeltaX = Math.abs(this.touchDeltaX);
      const absDeltaY = Math.abs(this.touchDeltaY);
      const activeSlide = this.slides[this.currentIndex];
      const swipeThreshold = activeSlide ? Math.min(64, activeSlide.offsetWidth * 0.16) : 48;

      if (absDeltaX > absDeltaY && absDeltaX >= swipeThreshold) {
        if (this.touchDeltaX < 0) {
          this.goBy(1);
        } else {
          this.goBy(-1);
        }
      }

      this.resetTouchState();
      this.startAutoplay();
    }

    handleTouchCancel() {
      if (!this.isTouchTracking) {
        return;
      }

      this.resetTouchState();
      this.startAutoplay();
    }

    setActive(index, options = {}) {
      if (!this.slides.length || index < 0 || index >= this.slides.length) {
        return;
      }

      if (index === this.currentIndex && !options.force) {
        return;
      }

      this.currentIndex = index;
      this.positionIndex = this.loop && this.slides.length > 1 ? this.slides.length + index : index;

      this.renderedSlides.forEach((slide) => {
        slide.classList.toggle('is-active', Number(slide.dataset.index) === index);
      });

      this.updateProgress();
      this.updateNavigationState();

      this.updateTrackPosition(Boolean(options.instant));
    }

    setupLoopTrack() {
      this.renderedSlides = this.slides;

      if (!this.loop || !this.track || this.slides.length < 2) {
        return;
      }

      const createClone = (slide) => {
        const clone = slide.cloneNode(true);
        clone.dataset.carouselClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');

        [clone, ...clone.querySelectorAll('*')].forEach((element) => {
          Array.from(element.attributes).forEach((attribute) => {
            if (attribute.name.startsWith('data-shopify-editor-')) {
              element.removeAttribute(attribute.name);
            }
          });
        });

        clone.querySelectorAll('img').forEach((image) => {
          image.loading = 'lazy';
        });
        return clone;
      };

      const leadingClones = this.slides.map(createClone);
      const trailingClones = this.slides.map(createClone);
      const leadingFragment = document.createDocumentFragment();
      const trailingFragment = document.createDocumentFragment();

      leadingClones.forEach((clone) => leadingFragment.appendChild(clone));
      trailingClones.forEach((clone) => trailingFragment.appendChild(clone));
      this.track.insertBefore(leadingFragment, this.slides[0]);
      this.track.appendChild(trailingFragment);
      this.renderedSlides = Array.from(this.track.querySelectorAll('[data-carousel-slide]'));
      this.positionIndex = this.slides.length;
    }

    goBy(direction) {
      if (!this.slides.length) {
        return;
      }

      if (this.isAnimating) {
        this.pendingDirection = direction;
        return;
      }

      if (!this.loop) {
        const index = Math.max(0, Math.min(this.currentIndex + direction, this.slides.length - 1));
        this.setActive(index);
        return;
      }

      const lastIndex = this.slides.length - 1;
      this.currentIndex = direction > 0
        ? (this.currentIndex + 1) % this.slides.length
        : (this.currentIndex - 1 + this.slides.length) % this.slides.length;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.setActive(this.currentIndex, { instant: true, force: true });
        return;
      }

      this.positionIndex += direction;
      this.isAnimating = true;

      this.renderedSlides.forEach((slide) => {
        slide.classList.toggle('is-active', Number(slide.dataset.index) === this.currentIndex);
      });
      this.updateProgress();
      this.updateNavigationState();
      this.updateTrackPosition(false);

      if (this.currentIndex !== 0 && this.currentIndex !== lastIndex) {
        return;
      }
    }

    handleTransitionEnd(event) {
      if (event.target !== this.track || event.propertyName !== 'transform') {
        return;
      }

      if (this.loop && this.slides.length > 1) {
        if (this.positionIndex >= this.slides.length * 2) {
          this.positionIndex = this.slides.length;
          this.updateTrackPosition(true);
        } else if (this.positionIndex < this.slides.length) {
          this.positionIndex = (this.slides.length * 2) - 1;
          this.updateTrackPosition(true);
        }
      }

      this.isAnimating = false;

      if (this.pendingDirection) {
        const direction = this.pendingDirection;
        this.pendingDirection = 0;
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => this.goBy(direction));
        });
      }
    }

    updateProgress() {
      if (!this.progressBar || !this.slides.length) {
        return;
      }

      const progressRatio = this.slides.length > 1 ? this.currentIndex / (this.slides.length - 1) : 0;
      const minimumProgress = 100 / this.slides.length;
      const progress = minimumProgress + progressRatio * (100 - minimumProgress);

      this.progressBar.style.width = `${progress}%`;
      this.progressBar.style.transform = '';
    }

    updateNavigationState() {
      const previousDisabled = !this.loop && this.currentIndex <= 0;
      const nextDisabled = !this.loop && this.currentIndex >= this.slides.length - 1;

      if (this.prevButton) {
        this.prevButton.classList.toggle('is-disabled', previousDisabled);
        this.prevButton.disabled = previousDisabled;
      }

      if (this.nextButton) {
        this.nextButton.classList.toggle('is-disabled', nextDisabled);
        this.nextButton.disabled = nextDisabled;
      }
    }

    updateTrackPosition(instant = false) {
      if (!this.track || !this.slides.length) {
        return;
      }

      const nextX = this.getTrackX(this.positionIndex);

      this.track.style.transitionDuration = instant ? '0ms' : '600ms';

      // Commit the intended duration before changing transform. This is
      // especially important for the invisible loop reset: without the
      // forced layout, browsers can reuse the previous 600ms transition and
      // visibly animate the track back to the real first slide.
      void this.track.offsetWidth;

      this.track.style.transform = `translate3d(${nextX}px, 0, 0)`;

      if (instant) {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            if (this.track) {
              this.track.style.transitionDuration = '';
            }
          });
        });
      }
    }

    getSideOffset() {
      const viewportWidth = window.innerWidth;

      if (viewportWidth <= 749) {
        return 24;
      }

      if (viewportWidth <= 989) {
        return 40;
      }

      return Math.max(50, ((window.innerWidth - 1440) / 2) + 50);
    }

    getTrackX(index) {
      const targetSlide = this.renderedSlides[index];
      const firstSlide = this.renderedSlides[0];

      if (!targetSlide || !firstSlide) {
        return 0;
      }

      return -(targetSlide.offsetLeft - firstSlide.offsetLeft);
    }

    observeVisibility() {
      if (!this.autoplayEnabled || this.slides.length < 2) {
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.isInViewport = true;
        this.startAutoplay();
        return;
      }

      this.intersectionObserver = new IntersectionObserver(this.handleIntersection, {
        threshold: [0, 0.25, 0.5]
      });
      this.intersectionObserver.observe(this);
    }

    handleIntersection(entries) {
      const entry = entries[0];

      if (!entry) {
        return;
      }

      this.isInViewport = entry.isIntersecting && entry.intersectionRatio >= 0.25;

      if (this.isInViewport) {
        this.startAutoplay();
        return;
      }

      this.stopAutoplay();
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.slides.length < 2 || !this.isInViewport || this.isPointerInside || this.isFocusInside) {
        this.stopAutoplay();
        return;
      }

      this.stopAutoplay();
      this.timer = window.setInterval(() => {
        if (!this.loop && this.currentIndex >= this.slides.length - 1) {
          this.stopAutoplay();
          return;
        }
        this.goBy(1);
      }, this.interval);
    }

    stopAutoplay() {
      if (!this.timer) {
        return;
      }

      window.clearInterval(this.timer);
      this.timer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    pauseAutoplay(event) {
      if (event.type === 'mouseenter') {
        this.isPointerInside = true;
      }

      if (event.type === 'focusin') {
        this.isFocusInside = true;
      }

      this.stopAutoplay();
    }

    resumeAutoplay(event) {
      if (event.type === 'mouseleave') {
        this.isPointerInside = false;
      }

      if (event.type === 'focusout' && !this.contains(event.relatedTarget)) {
        this.isFocusInside = false;
      }

      this.startAutoplay();
    }

    resetTouchState() {
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;
      this.isTouchTracking = false;
    }
  });
}


if (!window.sellingPointsCarouselBlockSelectBound) {
  window.sellingPointsCarouselBlockSelectBound = true;

  document.addEventListener('shopify:block:select', (event) => {
    const target = event.target instanceof Element ? event.target : null;

    if (!target) {
      return;
    }

    const slide = target.matches('[data-carousel-slide]') ? target : target.querySelector('[data-carousel-slide]');

    if (!slide) {
      return;
    }

    const carousel = slide.closest('selling-points-carousel');

    if (!carousel || typeof carousel.setActive !== 'function') {
      return;
    }
    const index = Number(slide.dataset.index);
    carousel.setActive(index, { instant: true });
  });
}
